import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { SocketService } from './socket.service';
import { GatewayService } from '../gateway.service';

export interface LockEntry {
    resource_type: string;
    resource_id: string;
    idagente: number;
    nombre_agente: string | null;
    socket_id?: string | null;
    acquired_at?: string;
    last_heartbeat?: string;
    expires_at?: string;
}

export interface AcquireResult {
    status: 'granted' | 'denied';
    lock: LockEntry;
    deny_reason?: 'holder' | 'session_lost' | 'disconnected';
}

const TTL_HEARTBEAT_MS = 60 * 1000;

@Injectable({ providedIn: 'root' })
export class ResourceLockService {

    locks$ = new BehaviorSubject<Map<string, LockEntry>>(new Map());

    private heartbeats = new Map<string, any>();         // key → interval id
    private lostCallbacks = new Map<string, () => void>(); // key → onLost handler
    private pendingAcquires = new Map<string, Array<(r: AcquireResult) => void>>();
    private connected = false;
    private hasEverConnected = false;
    private currentIdAgente: number | null = null;

    constructor(
        private socketService: SocketService,
        private gateway: GatewayService
    ) {}

    private keyOf(resource_type: string, resource_id: string): string {
        return `${resource_type}:${resource_id}`;
    }

    /**
     * Conecta al namespace /locks. Idempotente.
     * Llamar después de setear `usuarioPGV` en localStorage (en login).
     */
    connect(token: string, idagente: number): void {
        if (this.connected) return;
        this.currentIdAgente = idagente;

        // Garantizar que el socket de alarmas (namespace default) también esté
        // inicializado — su constructor ya no lo crea automáticamente para
        // no crashear si Login lo instancia sin sesión previa.
        this.socketService.initIfNeeded();

        const locksSocket = this.socketService.initLocks(token, idagente);
        if (!locksSocket) {
            console.error('[resource-lock] no se pudo inicializar el socket de locks');
            return;
        }

        locksSocket.on('connect', () => { this.connected = true; this.hasEverConnected = true; });
        locksSocket.on('disconnect', () => { this.connected = false; });

        locksSocket.on('lock:acquired', (lock: LockEntry) => {
            const m = new Map(this.locks$.value);
            m.set(this.keyOf(lock.resource_type, String(lock.resource_id)), lock);
            this.locks$.next(m);
        });

        locksSocket.on('lock:released', (payload: { resource_type: string; resource_id: string }) => {
            const m = new Map(this.locks$.value);
            m.delete(this.keyOf(payload.resource_type, String(payload.resource_id)));
            this.locks$.next(m);
        });

        locksSocket.on('lock:acquire:response', (payload: AcquireResult) => {
            const lock = payload.lock || ({} as LockEntry);
            const key = this.keyOf(lock.resource_type, String(lock.resource_id));
            const queue = this.pendingAcquires.get(key);
            if (queue && queue.length > 0) {
                const cb = queue.shift();
                if (cb) cb(payload);
                if (queue.length === 0) this.pendingAcquires.delete(key);
            }
        });

        locksSocket.on('lock:lost', (payload: { resource_type: string; resource_id: string; reason: string }) => {
            console.warn('[resource-lock] LOCK:LOST recibido', payload);
            const key = this.keyOf(payload.resource_type, String(payload.resource_id));
            const handler = this.lostCallbacks.get(key);
            if (handler) {
                console.warn('[resource-lock] ejecutando onLost callback para', key, '(reason:', payload.reason, ')');
                handler();
            }
            this.stopHeartbeat(payload.resource_type, payload.resource_id);
            const m = new Map(this.locks$.value);
            m.delete(key);
            this.locks$.next(m);
        });

        locksSocket.on('lock:heartbeat:response', (payload: { status: 'renewed' | 'lost' }) => {
            if (payload && payload.status === 'lost') {
                console.warn('[resource-lock] heartbeat:response = LOST');
            } else {
                console.log('[resource-lock] heartbeat:response = renewed');
            }
        });
    }

    disconnect(): void {
        this.connected = false;
        this.hasEverConnected = false;
        this.currentIdAgente = null;
        this.heartbeats.forEach((h) => clearInterval(h));
        this.heartbeats.clear();
        this.lostCallbacks.clear();
        this.pendingAcquires.clear();
        this.socketService.disconnectLocks();
        this.locks$.next(new Map());
    }

    /**
     * Solicita el lock de un recurso. Resuelve con granted o denied.
     * El cache local se actualiza vía broadcast `lock:acquired` (no hace falta updates manuales aquí).
     */
    acquire(resource_type: string, resource_id: string): Promise<AcquireResult> {
        return new Promise((resolve) => {
            const locksSocket = this.socketService.getLocksSocket();
            if (!locksSocket) {
                const reason = this.hasEverConnected ? 'disconnected' : 'session_lost';
                resolve({
                    status: 'denied',
                    lock: { resource_type, resource_id, idagente: -1, nombre_agente: null },
                    deny_reason: reason
                });
                return;
            }
            const key = this.keyOf(resource_type, String(resource_id));
            const queue = this.pendingAcquires.get(key) || [];
            queue.push(resolve);
            this.pendingAcquires.set(key, queue);
            locksSocket.emit('lock:acquire', { resource_type, resource_id: String(resource_id) });
        });
    }

    release(resource_type: string, resource_id: string): void {
        const locksSocket = this.socketService.getLocksSocket();
        if (!locksSocket) return;
        locksSocket.emit('lock:release', { resource_type, resource_id: String(resource_id) });
        this.stopHeartbeat(resource_type, resource_id);
    }

    startHeartbeat(resource_type: string, resource_id: string, onLost: () => void): void {
        const key = this.keyOf(resource_type, String(resource_id));
        this.lostCallbacks.set(key, onLost);
        if (this.heartbeats.has(key)) {
            console.log('[resource-lock] startHeartbeat: ya existe interval para', key);
            return;
        }

        const locksSocket = this.socketService.getLocksSocket();
        if (!locksSocket) {
            console.warn('[resource-lock] startHeartbeat: locksSocket null para', key);
            return;
        }

        console.log('[resource-lock] startHeartbeat: arrancando interval para', key, '(60s) connected=', locksSocket.connected);

        const emitHeartbeat = (label: string) => {
            if (locksSocket.connected) {
                locksSocket.emit('lock:heartbeat', { resource_type, resource_id: String(resource_id) });
                console.log('[resource-lock] heartbeat emit (' + label + '):', key);
            } else {
                console.warn('[resource-lock] heartbeat skipped (not connected):', key);
            }
        };

        // Primer heartbeat: si ya está conectado, emit inmediato; si no, esperar 'connect'
        if (locksSocket.connected) {
            emitHeartbeat('immediate');
        } else {
            locksSocket.once('connect', () => emitHeartbeat('after-connect'));
        }

        const handle = setInterval(() => emitHeartbeat('interval'), TTL_HEARTBEAT_MS);
        this.heartbeats.set(key, handle);
    }

    stopHeartbeat(resource_type: string, resource_id: string): void {
        const key = this.keyOf(resource_type, String(resource_id));
        const h = this.heartbeats.get(key);
        if (h) {
            clearInterval(h);
            this.heartbeats.delete(key);
        }
        this.lostCallbacks.delete(key);
    }

    /**
     * Carga el estado actual de locks desde el endpoint REST.
     * Llamar al abrir una vista que necesite el estado inicial.
     */
    async loadInitial(resource_type: string): Promise<void> {
        try {
            const res: any = await this.gateway.get('locksListar', { resource_type });
            if (res && res.response === 1 && Array.isArray(res.locks)) {
                const m = new Map<string, LockEntry>();
                res.locks.forEach((lock: LockEntry) => {
                    m.set(this.keyOf(lock.resource_type, String(lock.resource_id)), lock);
                });
                this.locks$.next(m);
            }
        } catch (e) {
            console.error('[resource-lock] loadInitial error', e);
        }
    }

    async forceRelease(resource_type: string, resource_id: string): Promise<boolean> {
        try {
            const res: any = await this.gateway.get('locksForceRelease', { resource_type, resource_id: String(resource_id) });
            return !!(res && res.response === 1 && res.released);
        } catch (e) {
            console.error('[resource-lock] forceRelease error', e);
            return false;
        }
    }

    isLocked(resource_type: string, resource_id: string | number): boolean {
        return this.locks$.value.has(this.keyOf(resource_type, String(resource_id)));
    }

    isLockedByMe(resource_type: string, resource_id: string | number): boolean {
        const entry = this.locks$.value.get(this.keyOf(resource_type, String(resource_id)));
        return !!(entry && this.currentIdAgente != null && Number(entry.idagente) === Number(this.currentIdAgente));
    }

    getHolder(resource_type: string, resource_id: string | number): LockEntry | null {
        return this.locks$.value.get(this.keyOf(resource_type, String(resource_id))) || null;
    }
}
